import { Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useActiveJobCount } from '@/api/jobs';
import { paths } from '@/app/paths';
import { Icon, Segmented } from '@/design-system';
import { NotificationsBell } from '@/features/notifications/NotificationsBell';
import { CommandPalette, usePaletteShortcut } from '@/features/search/CommandPalette';
import { cx } from '@/lib/cx';
import { useCurrentCrumbs } from './breadcrumbs';
import { useLocaleSwitch } from './useLocaleSwitch';
import s from './Header.module.css';

export function Header() {
  const { t } = useTranslation('shell');
  const crumbs = useCurrentCrumbs();
  const navigate = useNavigate();
  // Queued and running jobs, live from /admin/events (P1-E05).
  const running = useActiveJobCount();
  const { locale, setLocale } = useLocaleSwitch();
  const [searching, setSearching] = useState(false);
  usePaletteShortcut(useCallback(() => setSearching((v) => !v), []));

  return (
    <header className={s.header}>
      <ol className={s.crumbs} aria-label={t('breadcrumbs')}>
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <Fragment key={`${i}-${c.label}`}>
              <li>
                {c.to && !last ? (
                  <Link to={c.to} className={s.crumb}>
                    {c.label}
                  </Link>
                ) : (
                  <span className={cx(s.crumb, last && s.current)} aria-current={last ? 'page' : undefined}>
                    {c.label}
                  </span>
                )}
              </li>
              {!last && <Icon name="chevron-right" size={14} className={s.sep} />}
            </Fragment>
          );
        })}
      </ol>
      {/* The global search (P5-E06): the pill opens the command palette, as ⌘K / Ctrl+K does. */}
      <button type="button" className={s.search} aria-haspopup="dialog" aria-keyshortcuts="Meta+K Control+K" onClick={() => setSearching(true)}>
        <Icon name="search" size={15} />
        <span className={s.searchText}>{t('search_placeholder')}</span>
        <span className={s.kbd}>{t('search_kbd')}</span>
      </button>
      <CommandPalette open={searching} onClose={() => setSearching(false)} />
      <button type="button" className={s.jobs} title={t('jobs_title')} onClick={() => navigate(paths.dashboard())}>
        <span className={s.spinner} aria-hidden />
        {t('jobs', { count: running })}
      </button>
      <Segmented
        label={t('language')}
        variant="header"
        value={locale}
        onChange={setLocale}
        options={[
          { value: 'ru', label: 'RU' },
          { value: 'uz', label: 'UZ' },
        ]}
      />
      {/* P6-E02: the bell opens the notifications; its dot shows while something is unread. */}
      <NotificationsBell className={s.bell} dotClassName={s.bellDot} />
    </header>
  );
}
